import React, { useEffect, useState } from "react";
import { Loader2, ScanLine, User, ChevronLeft, ChevronRight } from "lucide-react";
import { teamApi } from "../../lib/events";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";

const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—";
const fmtAud = (c) => `A$${(c / 100).toFixed(2)}`;
const short = (s, n = 60) => (s && s.length > n ? s.slice(0, n) + "…" : s);

function ChangeRows({ changed }) {
  return (
    <div className="mt-1.5 space-y-0.5 border-l-2 border-[var(--gt-line)] pl-2">
      {Object.entries(changed).map(([field, v]) => (
        <div key={field} className="text-[11px] font-mono">
          <span className="font-bold">{field}</span>:{" "}
          <span className="line-through text-[var(--gt-ink-3)]">{String(v.old ?? "—")}</span>
          {" → "}
          <span className="text-[var(--gt-ink)]">{String(v.new ?? "—")}</span>
        </div>
      ))}
    </div>
  );
}

function RoleChange({ before, after }) {
  const added = (after.permissions || []).filter((p) => !(before.permissions || []).includes(p));
  const removed = (before.permissions || []).filter((p) => !(after.permissions || []).includes(p));
  return (
    <div className="mt-1.5 space-y-0.5 border-l-2 border-[var(--gt-line)] pl-2 text-[11px]">
      {before.role !== after.role && (
        <div><span className="font-bold">Role</span>: <span className="line-through text-[var(--gt-ink-3)]">{before.role}</span> → {after.role}</div>
      )}
      {added.length > 0 && <div className="text-emerald-700">+ {added.join(", ")}</div>}
      {removed.length > 0 && <div className="text-[var(--gt-accent)]">− {removed.join(", ")}</div>}
    </div>
  );
}

function ActivityRow({ item }) {
  const m = item.meta || {};
  const refs = [];
  if (m.order_id) refs.push(`Order ${m.order_id.slice(0, 8)}`);
  if (m.ticket_code) refs.push(`Ticket ${m.ticket_code}`);
  if (m.export) refs.push(m.export);
  if (m.email && item.action.startsWith("team_")) refs.push(m.email);
  if (m.label) refs.push(`"${m.label}"`);
  if (m.amount_cents) refs.push(fmtAud(m.amount_cents));
  return (
    <div className="border-b border-[var(--gt-line)] py-3" data-testid={`activity-row-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-bold flex items-center gap-1.5">
          {item.actor_type === "scanner_token" ? <ScanLine size={12} /> : <User size={12} />}
          {item.action_label}
          {m.manual && <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-[var(--gt-bg-surface)] border border-[var(--gt-line)]">manual</span>}
        </div>
        <div className="text-[10px] text-[var(--gt-ink-3)] whitespace-nowrap">{fmtWhen(item.created_at)}</div>
      </div>
      {item.actor_type === "scanner_token" ? (
        <div className="text-[11px] text-[var(--gt-ink-2)] mt-0.5">
          via scanner link: <span className="font-semibold">{item.via_scanner_link}</span>
          {item.scanner_link_created_by && (
            <span className="text-[var(--gt-ink-3)]"> · link created by {item.scanner_link_created_by}</span>
          )}
        </div>
      ) : (
        item.performed_by && (
          <div className="text-[11px] text-[var(--gt-ink-2)] mt-0.5">
            by {item.performed_by_name ? `${item.performed_by_name} (${item.performed_by})` : item.performed_by}
          </div>
        )
      )}
      {refs.length > 0 && <div className="text-[11px] text-[var(--gt-ink-2)] mt-0.5">{refs.join(" · ")}</div>}
      {m.changed && Object.keys(m.changed).length > 0 && <ChangeRows changed={m.changed} />}
      {m.before && m.after && <RoleChange before={m.before} after={m.after} />}
      {m.previous_role && (
        <div className="text-[11px] text-[var(--gt-ink-3)] mt-0.5">Previous role: {m.previous_role}</div>
      )}
      {(item.ip || item.user_agent) && (
        <div className="text-[10px] text-[var(--gt-ink-3)] mt-1 font-mono">
          {[item.ip, short(item.user_agent, 70)].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}

export const TeamActivityDrawer = ({ eventId, member, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    teamApi.activity(eventId, member.id, { action, date_from: from, date_to: to, page, limit: 20 })
      .then(setData)
      .catch(() => setData({ items: [], total: 0, pages: 1, available_actions: {} }))
      .finally(() => setLoading(false));
  }, [eventId, member.id, action, from, to, page]);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="team-activity-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading tracking-tight">Activity log</SheetTitle>
          <SheetDescription data-testid="activity-member-label">
            {member.invited_name || member.invited_email} · {member.role_label || member.role}
            {member.invitation_status === "revoked" && " · removed"}
          </SheetDescription>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
          <select className="gt-input col-span-2" value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }} data-testid="activity-action-filter">
            <option value="">All actions</option>
            {Object.entries(data?.available_actions || {}).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <input type="date" className="gt-input" value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="activity-from-input" />
          <input type="date" className="gt-input" value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="activity-to-input" />
        </div>

        {loading && <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin" /></div>}
        {!loading && data && data.items.length === 0 && (
          <div className="text-xs text-[var(--gt-ink-3)] py-8 text-center" data-testid="activity-empty">
            No activity found for this member{action || from || to ? " with these filters" : ""}.
          </div>
        )}
        {!loading && data && data.items.length > 0 && (
          <div className="mt-2" data-testid="activity-list">
            {data.items.map((it) => <ActivityRow key={it.id} item={it} />)}
          </div>
        )}

        {data && data.total > 0 && (
          <div className="flex items-center justify-between mt-4 text-xs">
            <span className="text-[var(--gt-ink-3)]" data-testid="activity-total">
              {data.total} record{data.total === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="gt-btn-ghost text-xs disabled:opacity-30" data-testid="activity-prev-btn">
                <ChevronLeft size={13} /> Prev
              </button>
              <span data-testid="activity-page">Page {data.page} / {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
                className="gt-btn-ghost text-xs disabled:opacity-30" data-testid="activity-next-btn">
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
